/**
 * React Query hook — ``GET /auth/tenants/{slug}/exists``.
 *
 * Returns whether a tenant slug is registered in the DB.
 * Used by ``RequireValidSlug`` to gate public auth routes before any
 * auth form renders.
 *
 * Network 404 is treated as ``exists: false`` (conservative fail-closed).
 * ``staleTime: 5 min`` so slug→login → slug→forgot-password navigation
 * doesn't re-fetch within the same session.
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

interface TenantExistsResponse {
  exists: boolean;
}

export function useTenantExists(slug: string | undefined) {
  return useQuery<TenantExistsResponse, ApiError>({
    queryKey: ["auth", "tenantExists", slug],
    queryFn: async () => {
      if (!slug) return { exists: false };
      try {
        const r = await apiClient.get<TenantExistsResponse>(
          `/auth/tenants/${encodeURIComponent(slug)}/exists`,
        );
        return r.data;
      } catch (err) {
        // Fail-closed: any error (404 network, 500 server, network timeout)
        // returns exists=false so the user sees /404 rather than a spinner.
        // The endpoint is best-effort UX protection — blocking on transient
        // errors would be worse than showing the NotFound page.
        if (err instanceof ApiError) {
          return { exists: false };
        }
        // Non-ApiError (network failure, CORS, etc.) — also fail closed.
        return { exists: false };
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
}
